import { useQueryClient } from '@tanstack/react-query';
// mantine
import { Badge, Button, HoverCard, UnstyledButton } from '@mantine/core';
import { modals } from '@mantine/modals';
// icons
import { Download, RefreshCw } from 'lucide-react';
// components
import Tasks from '@/pages/AKB/components/Toolbar/components/ImportTextButton/components/Tasks';
import ImportTextForm from '@/pages/AKB/components/Toolbar/components/ImportTextButton/components/ImportTextForm';
// utils
import {
    useAKBTasksQuery,
    useImportAKBFileMutation
} from '@/services/akb/akb.queries.ts';

interface ImportTextButtonProps {
    personaId: string;
}

const handleCloseAll = () => {
    modals.closeAll();
};

const TASKS_TO_DISPLAY = 5;

const ImportTextButton = ({ personaId }: ImportTextButtonProps) => {
    const queryClient = useQueryClient();
    const {
        data: tasks = [],
        isLoading,
        isFetching,
        refetch: refetchTasks
    } = useAKBTasksQuery({
        persona_id: personaId
    });
    const importMutation = useImportAKBFileMutation();

    const activeCount = tasks.filter((task) =>
        ['created', 'pending', 'processing'].includes(task.status)
    ).length;

    const handleSubmit = async ({
        file,
        labels
    }: {
        file: File;
        labels: string[];
    }) => {
        const formData = new FormData();
        formData.append('file', file);
        // @ts-expect-error the only format BE accepts
        formData.append(`labels`, labels);
        return importMutation.mutateAsync(
            {
                personaId,
                data: formData
            },
            {
                onSuccess: () => {
                    refetchTasks();
                    queryClient.invalidateQueries({
                        queryKey: ['akb/topics']
                    });
                    modals.closeAll();
                }
            }
        );
    };

    const handleClick = () => {
        modals.open({
            title: 'Import Facts',
            size: '50%',
            children: (
                <ImportTextForm
                    onCancel={handleCloseAll}
                    onSubmit={handleSubmit}
                />
            )
        });
    };

    return (
        <HoverCard
            withArrow
            disabled={isLoading || tasks.length === 0}
            shadow="md"
            width={300}
        >
            <HoverCard.Target>
                <Button
                    className="shrink-0"
                    disabled={!personaId}
                    leftSection={<Download />}
                    loading={importMutation.isPending}
                    rightSection={
                        activeCount > 0 ? (
                            <Badge color="blue" size="xs">
                                {activeCount}
                            </Badge>
                        ) : undefined
                    }
                    variant="default"
                    onClick={handleClick}
                >
                    Import Facts
                </Button>
            </HoverCard.Target>

            <HoverCard.Dropdown className="p-0">
                <div className="flex justify-end px-4 py-2 text-gray-6">
                    <UnstyledButton
                        className="flex items-center gap-2 text-xs"
                        onClick={() => {
                            refetchTasks();
                        }}
                    >
                        <RefreshCw
                            className={isFetching ? 'animate-spin' : ''}
                        />
                        Refresh tasks
                    </UnstyledButton>
                </div>
                <Tasks tasks={tasks.slice(-TASKS_TO_DISPLAY)} />
            </HoverCard.Dropdown>
        </HoverCard>
    );
};

export default ImportTextButton;
